import { useState, useEffect, useCallback } from 'react';
import { inventoryService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialInventory } from '../data/inventory';
import type { InventoryItem } from '../types';

interface UseInventoryReturn {
  inventory: InventoryItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (item: Omit<InventoryItem, 'id'>) => Promise<InventoryItem>;
  update: (id: number, updates: Partial<InventoryItem>) => Promise<InventoryItem>;
  remove: (id: number) => Promise<void>;
}

export const useInventory = (enabled = true): UseInventoryReturn => {
  const [inventory, setInventory] = useState<InventoryItem[]>(initialInventory);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInventory = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setInventory(initialInventory);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getAll();
      setInventory(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load inventory';
      setError(errorMessage);
      console.error('Error fetching inventory:', err);
      setInventory(initialInventory);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchInventory();
  }, [fetchInventory, enabled]);

  const create = useCallback(async (item: Omit<InventoryItem, 'id'>): Promise<InventoryItem> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newItem: InventoryItem = {
        ...item,
        id: Math.max(...inventory.map(i => i.id), 0) + 1,
      };
      setInventory(prev => [...prev, newItem]);
      return newItem;
    }

    setError(null);
    try {
      const newItem = await inventoryService.create(item);
      setInventory(prev => [...prev, newItem]);
      return newItem;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create inventory item';
      setError(errorMessage);
      throw err;
    }
  }, [inventory]);

  const update = useCallback(async (id: number, updates: Partial<InventoryItem>): Promise<InventoryItem> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const updatedItem = inventory.find(i => i.id === id);
      if (!updatedItem) throw new Error('Inventory item not found');
      const newItem = { ...updatedItem, ...updates };
      setInventory(prev => prev.map(i => i.id === id ? newItem : i));
      return newItem;
    }

    setError(null);
    try {
      const updatedItem = await inventoryService.update(id, updates);
      setInventory(prev => prev.map(i => i.id === id ? updatedItem : i));
      return updatedItem;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update inventory item';
      setError(errorMessage);
      throw err;
    }
  }, [inventory]);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setInventory(prev => prev.filter(i => i.id !== id));
      return;
    }

    setError(null);
    try {
      await inventoryService.delete(id);
      setInventory(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete inventory item';
      setError(errorMessage);
      throw err;
    }
  }, []);

  return {
    inventory,
    loading,
    error,
    refresh: fetchInventory,
    create,
    update,
    remove,
  };
};

